import {SearchField} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {useDebouncer} from "@tanstack/react-pacer";
import {useEffect, useState} from "react";

import {componentMessages} from "../../components.messages";

interface TableSearchFieldProps {
  /** The committed search value from the external source of truth (e.g. URL). */
  value: string;
  /**
   * Called with the new search string after the debounce delay has elapsed.
   * Also called immediately (bypassing debounce) when the user clears the field.
   */
  onDebouncedChange: (value: string) => void;
  /** Debounce wait in milliseconds (default: 300). */
  debounceMs?: number;
  /** Placeholder text shown in the empty input. */
  placeholder?: string;
  /** Accessible label for the search field (default: "Search"). */
  label?: string;
}

/**
 * TableSearchField — debounced search input for data table toolbars.
 *
 * Wraps HeroUI's `SearchField` compound component and internalizes the full
 * debounce lifecycle via `@tanstack/react-pacer`'s `useDebouncer`. Callers
 * only need to provide the committed value (e.g. from the URL) and a handler
 * for when the debounced value settles — no manual setTimeout or useEffect
 * needed in the page.
 *
 * **External sync**: when `value` changes from an external source (e.g. browser
 * back/forward), the input resets and any pending debounce is cancelled so stale
 * navigation is never triggered.
 *
 * **Clear**: bypasses the debounce and fires `onDebouncedChange("")` immediately
 * for instant feedback.
 *
 * @example
 * ```tsx
 * <TableSearchField
 *   value={rawSearch.q ?? ""}
 *   onDebouncedChange={(val) =>
 *     navigate({search: (prev) => ({...prev, page: 1, q: val.trim() || undefined})})
 *   }
 *   placeholder="Search by name or email…"
 *   label="Search users"
 * />
 * ```
 */
export function TableSearchField({
  debounceMs = 300,
  label,
  onDebouncedChange,
  placeholder,
  value,
}: TableSearchFieldProps) {
  const {t} = useI18n();
  const [inputValue, setInputValue] = useState(value);
  const resolvedLabel = label ?? t(componentMessages.tableSearchLabel);
  const resolvedPlaceholder = placeholder ?? t(componentMessages.tableSearchPlaceholder);

  const debouncer = useDebouncer(onDebouncedChange, {wait: debounceMs});

  // When an external source changes the committed value (e.g. browser back/forward),
  // reset the visible input and cancel any pending debounce to prevent stale navigation.
  useEffect(() => {
    setInputValue(value);
    debouncer.cancel();
  }, [value, debouncer]);

  function handleChange(val: string) {
    setInputValue(val);
    debouncer.maybeExecute(val);
  }

  function handleClear() {
    setInputValue("");
    debouncer.cancel();
    onDebouncedChange("");
  }

  return (
    <SearchField
      aria-label={resolvedLabel}
      className="md:max w-full"
      fullWidth
      onChange={handleChange}
      onClear={handleClear}
      value={inputValue}
    >
      <SearchField.Group>
        <SearchField.SearchIcon />
        <SearchField.Input placeholder={resolvedPlaceholder} />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  );
}
